import { useEffect, useRef } from 'react';
import { useSharedMapContext } from './shared-map';

interface SharedMapEventHandlers {
  onClick?: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onZoomChanged?: () => void;
  onMouseUp?: () => void;
}

/**
 * Hook to manage event listeners on the shared map.
 * Uses refs for handlers to avoid recreating listeners on every render.
 */
export function useSharedMapEvents(handlers: SharedMapEventHandlers) {
  const { map } = useSharedMapContext();
  const handlersRef = useRef(handlers);

  // Keep handlers ref up to date
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  useEffect(() => {
    if (!map) return;

    const googleMapsEvent = google.maps.event;
    const listeners: google.maps.MapsEventListener[] = [];

    if (handlersRef.current.onClick) {
      listeners.push(
        googleMapsEvent.addListener(map, 'click', () => handlersRef.current.onClick?.()),
      );
    }

    if (handlersRef.current.onDragStart) {
      listeners.push(
        googleMapsEvent.addListener(map, 'dragstart', () => handlersRef.current.onDragStart?.()),
      );
    }

    if (handlersRef.current.onDragEnd) {
      listeners.push(
        googleMapsEvent.addListener(map, 'dragend', () => handlersRef.current.onDragEnd?.()),
      );
    }

    if (handlersRef.current.onZoomChanged) {
      listeners.push(
        googleMapsEvent.addListener(map, 'zoom_changed', () =>
          handlersRef.current.onZoomChanged?.(),
        ),
      );
    }

    if (handlersRef.current.onMouseUp) {
      listeners.push(
        googleMapsEvent.addListener(map, 'mouseup', () => handlersRef.current.onMouseUp?.()),
      );
    }

    return () => {
      listeners.forEach((listener) => googleMapsEvent.removeListener(listener));
    };
  }, [map]);
}
